/**
 * Toucan Reads — how far this device has got into each saved thing.
 *
 * `localStorage`, not the synced document: a place changes on every scroll
 * event, and a phone half way through a paper and a laptop at the top of it
 * are both right about where their reader is.
 */

import type { ScrollRestore } from "toucan";

const PLACE_KEY = "reads-place";

/** Where a reader had got to in one item. */
export interface Place {
  /** What to put the reading pane's scroll offset back to, in pixels. */
  top: number;
  /** How far through the content that offset is, 0 to 1. */
  through: number;
}

/** A reader that has not been opened here, or has not moved off the top. */
const START: Place = { top: 0, through: 0 };

/**
 * How far through an item counts as having read into it rather than glanced at
 * it: enough to mark it read, and enough for its card to be worth drawing a
 * progress bar on.
 */
export const READ_INTO = 0.1;

/**
 * Whether a stored value is a place. Anyone with a developer console can leave
 * something else under this key, and an article is worth more than the offset
 * it opens at.
 */
function isPlace(value: unknown): value is Place {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { top, through } = value as Partial<Place>;
  return typeof top === "number" && typeof through === "number";
}

/** Every place this device holds, keyed by item id. */
function places(): Record<string, unknown> {
  const raw = localStorage.getItem(PLACE_KEY);
  if (raw === null) {
    return {};
  }
  try {
    const stored: unknown = JSON.parse(raw);
    return typeof stored === "object" && stored !== null
      ? (stored as Record<string, unknown>)
      : {};
  } catch {
    // Half-written, or somebody else's. Start again rather than refusing to
    // open the article.
    return {};
  }
}

/** Where this device left off in an item, or the top of it. */
export function placeIn(id: string): Place {
  const stored = places()[id];
  return isPlace(stored) ? stored : START;
}

/** Remember where the reader has got to in an item. */
export function rememberPlace(id: string, place: Place): void {
  localStorage.setItem(PLACE_KEY, JSON.stringify({ ...places(), [id]: place }));
}

/**
 * The restore putting a reading pane back where this device left off, while
 * one is running. The reader page starts it, and the shell's scroll handler
 * does not count its scrolling as the reader's (see `Shell.tsx`).
 */
let placing: ScrollRestore | undefined;

/** Say which restore is putting a pane back, or pass undefined for none. */
export function notePlacing(restore: ScrollRestore | undefined): void {
  placing = restore;
}

/** Whether the current scroll event is a restore's rather than a reader's. */
export function beingPlaced(): boolean {
  return placing?.restoring ?? false;
}

/**
 * How far through an item this device is, where that is far enough to be worth
 * saying. A reader that has barely moved has nothing to report.
 */
export function progressIn(id: string): number | undefined {
  const { through } = placeIn(id);
  return through < READ_INTO ? undefined : through;
}
